import type { Persister, PersistedClient } from "@tanstack/react-query-persist-client";
import { getLocalForageStorage } from "../../utils/persistence/get-localforage-instance";
import { ensureLocalDataOwnerReconciled } from "../../utils/persistence/local-data-owner";

/**
 * localforage key the persisted TanStack Query cache is stored under. Namespaced/prefixed
 * distinctly from redux-persist's own `persist:root` key (see `redux/createStore.ts`) — both
 * write into the same physical IndexedDB database (via the shared `get-localforage-instance.ts`
 * loader) but must not collide on key name.
 */
const USER_STATE_QUERY_CACHE_STORAGE_KEY = "tanstack-query-user-state-cache";

/**
 * Custom TanStack Query `Persister` (see `@tanstack/react-query-persist-client`'s `Persister`
 * type) backed by the same localforage/IndexedDB instance `redux/createStore.ts` already uses
 * for anonymous users' saved/viewed/applied job state — see `get-localforage-instance.ts` for why
 * that shared loader exists.
 *
 * Deliberately hand-rolled rather than pulling in `@tanstack/query-async-storage-persister`:
 * localforage's IndexedDB backend stores structured-cloneable values directly, so there is no
 * need for that package's string serialize/deserialize step — `persistClient` hands the
 * `PersistedClient` object straight to `localForage.setItem`.
 *
 * What actually lands in this store is scoped by `UserProvider.tsx`'s
 * `dehydrateOptions.shouldDehydrateQuery` (see `userStatePersistenceScope.ts`) to just the
 * `userStateQueryKey` query — this persister itself has no opinion on that, it persists whatever
 * dehydrated client state it's handed.
 *
 * NOTE on restore-timing safety: it is tempting to add a "resolves once restoreClient() finishes"
 * signal here for mutations to await. Don't — that was tried and was subtly wrong. Resolving
 * inside `restoreClient()`'s own completion fires *before* `PersistQueryClientProvider` has
 * actually called `hydrate()` with the restored data (that happens in a later microtask, since
 * `restoreClient()`'s caller only calls `hydrate()` *after* its own `await` of this function
 * settles) — so a mutation gated on that signal could still write into the cache in the gap
 * between "disk read finished" and "hydrate ran," silently discarding the restored data anyway.
 * The correct signal is `useIsRestoring()` from `@tanstack/react-query-persist-client`, which only
 * flips false once hydration has genuinely completed — see `query/UserProvider.tsx`'s
 * `waitForUserStateRestored`/`useWaitForUserStateRestored` for where that's wired up.
 */
export function createUserStatePersister(): Persister {
  const storage = getLocalForageStorage();

  return {
    // Every op waits for the owner check (utils/persistence/local-data-owner.ts), so a previous
    // account's cache is wiped before it can be restored, and a fresh write is never wiped after.
    async persistClient(persistedClient: PersistedClient): Promise<void> {
      await ensureLocalDataOwnerReconciled();
      await storage.setItem(USER_STATE_QUERY_CACHE_STORAGE_KEY, persistedClient);
    },
    async restoreClient(): Promise<PersistedClient | undefined> {
      await ensureLocalDataOwnerReconciled();
      const value = await storage.getItem(USER_STATE_QUERY_CACHE_STORAGE_KEY);
      return (value as PersistedClient | null | undefined) ?? undefined;
    },
    async removeClient(): Promise<void> {
      await ensureLocalDataOwnerReconciled();
      await storage.removeItem(USER_STATE_QUERY_CACHE_STORAGE_KEY);
    },
  };
}
